import { useGSAP } from '@gsap/react';
import gsap from 'gsap';

import { graphql, useStaticQuery } from 'gatsby';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ALL_LANGS, INSIGHTS_PAGE_URL } from '../../../constants';
import { ModuleBgColor } from '../../../templates/Page';
import { PressRelease } from '../../../templates/pages/insights-page';
import { sortBy } from '../../../utils/nodash';
import { formatDate } from '../../../utils/projectUtils';
import { localize } from '../../../utils/sanity';
import { clsx, urlJoin } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import Card from '../Card';
import ModuleLayout from '../ModuleLayout';
import TextLink from '../TextLink';
import * as styles from './FeaturedInModule.module.scss';

export const FeaturedInModuleFragment = graphql`
  fragment FeaturedInModule on SanityPageFeaturedInModule {
    title {
      ...LocaleStringFragment
    }
    pressReleasesToUse
    pressReleases {
      ...PressRelease
    }
    buttonText {
      ...LocaleStringFragment
    }
  }
`;

export type FeaturedInModuleProps = {
  title: string;
  buttonText?: string;
  className?: string;
} & (
  | {
      pressReleasesToUse: 'latestThree';
      pressReleases?: never;
    }
  | {
      pressReleasesToUse: 'chooseManually';
      pressReleases: Array<PressRelease>;
    }
);

interface QueryData {
  allSanityPressRelease: {
    nodes: Array<PressRelease>;
  };
}

export function getModuleBgColor(props: FeaturedInModuleProps): ModuleBgColor {
  /**
   * The purpose of this function is to let other modules know which background color this module has.
   * Knowing this, we can use this function to make decisions about the layout and spacing between modules.
   */
  return 'white';
}

function FeaturedInModule(props: FeaturedInModuleProps & CommonModuleProps): React.ReactElement {
  const queryData = useStaticQuery<QueryData>(graphql`
    {
      allSanityPressRelease {
        nodes {
          ...PressRelease
        }
      }
    }
  `);

  const {
    title,
    pressReleasesToUse,
    pressReleases,
    buttonText,
    className,
    previousModuleBgColor,
    moduleId,
  } = props;

  const { i18n, t } = useTranslation();

  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: QueryData = localize(queryData, langs);

  const allPressReleasesSorted = sortBy(
    localizedData.allSanityPressRelease?.nodes,
    pressRelease => pressRelease.date,
    'desc',
  );

  const containerRef = useRef<HTMLElement | null>(null);

  const pressReleasesToShow =
    pressReleasesToUse === 'chooseManually' ? pressReleases : allPressReleasesSorted.slice(0, 3);

  useGSAP(
    () => {
      if (pressReleasesToShow.length < 1) {
        return; // avoid errors on console
      }

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: `.${styles.contentContainer}`,
          start: 'top 85%',
        } satisfies ScrollTrigger.Vars,
      });

      tl.from(`.${styles.card}`, {
        x: 40,
        autoAlpha: 0,
        stagger: 0.2,
        duration: 0.6,
      });
    },
    { scope: containerRef },
  );

  return pressReleasesToShow.length > 0 ? (
    <ModuleLayout
      id={moduleId}
      moduleRef={containerRef}
      className={clsx(className, styles.container)}
      contentClassName={styles.contentContainer}
      childrenClassName={styles.contentWrapper}
      currentModuleBgColor={getModuleBgColor(props)}
      previousModuleBgColor={previousModuleBgColor}
      title={title}
    >
      <div className={styles.cardsContainer}>
        {pressReleasesToShow.map((pressRelease, i) => (
          <React.Fragment key={i}>
            <Card
              className={styles.card}
              title={pressRelease.title}
              superTitle={formatDate(pressRelease.date)}
              image={pressRelease.image}
              link={{
                linkType: 'externalLink',
                externalLink: { url: pressRelease.url },
                text: t('readMoreButton.title', 'Ler Mais'),
              }}
              withRoundedCorner
              withColoredBackground
            />
          </React.Fragment>
        ))}
      </div>
      <TextLink to={urlJoin(INSIGHTS_PAGE_URL)} withArrowIcon className={styles.link}>
        {buttonText || t('featured_in_module.see_all_articles', 'Ver todos os artigos')}
      </TextLink>
    </ModuleLayout>
  ) : (
    <></>
  );
}

export default FeaturedInModule;
